import React, { useEffect, useRef, JSX } from 'react';
import { colors, fonts, glyphs } from '../../Styles';
import { createPopper } from '@popperjs/core';
import { DataValue } from '../../Definitions';
import { createPortal } from 'react-dom';

interface popoverProps {
  content: DataValue['value']
  popoverHeader: string
  popoverContent: JSX.Element
  isHovered: boolean
  referenceRef: React.RefObject<HTMLDivElement | null>
}
/**
 * PrimaryPopover
 * @param content The element or text to trigger the popover on when clicked.
 * @param popoverHeader The title displayed in the header of the popover.
 * @param popoverContent The JSX content to render inside the popover body.
 * @param overrides Optional styles to customize the popover background color and container styling.
 * @returns A clickable element that shows a styled popover with a header and content when toggled.
 */
export default function SecondaryPopover({content, popoverHeader, popoverContent, isHovered, referenceRef}:popoverProps) {
  const popperRef = useRef<HTMLDivElement | null>(null);
  const arrowRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isHovered && referenceRef.current && popperRef.current && arrowRef.current) {
      createPopper(referenceRef.current, popperRef.current, {
        placement: "top",
        strategy: 'fixed',
        modifiers: [
          { name: "offset", options: { offset: [0, 14] }},
          { name: "preventOverflow", options: { boundary: document.body }},
          { name: "arrow", options: { element: arrowRef.current }}
        ],
      });
    }
  }, [isHovered, referenceRef]);

  return ( <>
    {content}
    {createPortal(
      <div ref={popperRef} style={{
        width: 'fit-content',
        height: 'fit-content',
        backgroundColor: colors.greyDarkPrimary,
        zIndex: "9999",
        borderRadius: '8px',
        boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
        opacity: isHovered ? 1 : 0,
        visibility: isHovered ? 'visible' : 'hidden',
        transition: 'opacity 0.3s ease, visibility 0.3s ease',
        pointerEvents: isHovered ? 'auto' : 'none',
        position: "relative",
        border: "3px solid " + colors.blueDarkPrimary,
        overflow: "visible",
      }}>
        <div style={{width: "100%", height: "20px", backgroundColor: colors.blueDarkPrimary, display: "flex", alignItems: "center", justifyContent: "center", ...fonts.header, ...fonts.fontSize4, color: colors.greyLightTertiary}}>{popoverHeader}</div>
        <div style={{padding: "10px"}}>{popoverContent}</div>
        <div ref={arrowRef} style={{ zIndex: "0", position: "absolute", bottom: "-15px", width: "15px", height: "15px", pointerEvents: "none" }} data-popper-arrow>
          <glyphs.ArrowSmall rotate={90} fillColor={colors.blueDarkPrimary} />
        </div>
      </div>, document.body)
    }
  </> );
}